/**
 * List of allowed app hostnames for join9am environments and the current origin.
 * Used to identify internal app links.
 */
export const APP_LINK_URLS: string[] = [
  "https://app.join9am.com",
  "https://app.dev.join9am.com",
  "https://app.qa.join9am.com",
  "https://app.9am.health",
  "https://app.dev.9am.health",
  "https://app.qa.9am.health",
  location.origin
].map((url) => new URL(url).hostname);

/**
 * List of allowed CMS API hostnames.
 * Used to identify links to the CMS or Sanity backend.
 */
export const CMS_API_LINK_URLS: string[] = [
  "https://cms.join9am.com",
  "https://sanity-for-9am.vercel.app"
].map((url) => new URL(url).hostname);

/**
 * List of allowed website hostnames.
 * Used to identify links to the main marketing website.
 */
export const WEBSITE_LINK_URLS: string[] = ["https://join9am.com"].map(
  (url) => new URL(url).hostname
);

/**
 * List of allowed API hostnames for join9am environments.
 * Used to identify backend API links.
 */
export const API_LINK_URLS: string[] = [
  "https://api.us-east.prod.join9am.com",
  "https://api.dev.join9am.com",
  "https://api.qa.join9am.com",
  "https://api.us-east.prod.9am.health",
  "https://api.dev.9am.health",
  "https://api.qa.9am.health"
].map((url) => new URL(url).hostname);

/**
 * List of allowed AperoHealth hostnames.
 * Used to identify links to the AperoHealth system.
 */
export const APERO_LINK_URLS: string[] = [
  "https://9am-health-test.aperohealth.com",
  "https://9am-health.aperohealth.com"
].map((url) => new URL(url).hostname);

/**
 * Regular expression to match full URLs (http/https) in text.
 * Note: This regex does not match all possible valid URLs, but covers most common cases.
 */
export const URL_REGEX_FULL =
  // eslint-disable-next-line no-useless-escape
  /https?:\/\/[a-zA-Z0-9\/\$\%\-\_\.\+\!\*\';\?\:\@\=\&\#?]+/g;

/**
 * Safely parses a string or URL into a URL object, optionally using a base URL.
 * Attempts to decode the string if the first parse fails.
 * @param url - The URL string or URL object to parse.
 * @param base - Optional base URL for relative URLs.
 * @returns {URL} The parsed URL object.
 * @throws {Error} If the input cannot be parsed as a valid URL.
 */
export const saferUrl = (url: string | URL, base?: string | URL): URL => {
  if (url instanceof URL) {
    return url;
  }

  try {
    return new URL(url, base);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`Error parsing URL: ${url}`, e);
  }

  if (typeof url === "string") {
    try {
      return new URL(decodeURIComponent(url), base);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`Error parsing URL: ${url}`, e);
    }
  }

  throw new Error(`Invalid URL: ${url}`);
};

/**
 * Checks if a link matches any of the provided hostnames.
 * @param link - The link to check (string or URL).
 * @param urls - Array of allowed hostnames.
 * @returns {boolean} True if the link's hostname matches one in the list.
 */
const checkLink = (link: string | URL, urls: string[]): boolean => {
  if (!link) return false;

  try {
    const url = saferUrl(link);
    return urls.some((appLinkUrl) => {
      return url.hostname === appLinkUrl;
    });
  } catch (e: unknown) {
    if (link.toString().startsWith("#")) return false;
    if (link.toString().startsWith("/app")) return false;

    // eslint-disable-next-line no-console
    console.error(`Error checking link: ${link}`);
    // eslint-disable-next-line no-console
    console.error(e);
    return false;
  }
};

/**
 * Determines if a link is an internal app link.
 * @param link - The link to check (string or URL).
 * @returns {boolean} True if the link is an app link.
 */
export const isAppLink = (link: string | URL): boolean => {
  return checkLink(link, APP_LINK_URLS);
};

/**
 * Determines if a link is a CMS API link.
 * @param link - The link to check (string or URL).
 * @returns {boolean} True if the link is a CMS API link.
 */
export const isCmsApiLink = (link: string | URL): boolean => {
  return checkLink(link, CMS_API_LINK_URLS);
};

/**
 * Determines if a link is an API link.
 * @param link - The link to check (string or URL).
 * @returns {boolean} True if the link is an API link.
 */
export const isApiLink = (link: string | URL): boolean => {
  return checkLink(link, API_LINK_URLS);
};

/**
 * Determines if a link is an AperoHealth link.
 * @param link - The link to check (string or URL).
 * @returns {boolean} True if the link is an AperoHealth link.
 */
export const isAperoLink = (link: string | URL): boolean => {
  return checkLink(link, APERO_LINK_URLS);
};

/**
 * Determines if a link is a blog/website link.
 * @param link - The link to check (string or URL).
 * @returns {boolean} True if the link is a blog/website link.
 */
export const isBlogLink = (link: string | URL): boolean => {
  return checkLink(link, WEBSITE_LINK_URLS);
};

/**
 * Creates a new URL for opening a popup iframe in the app, embedding the given URL.
 * @param url - The URL to embed in the popup.
 * @returns {URL} The constructed popup URL.
 */
export const toAppIframePopup = (url: URL): URL => {
  const newUrl = saferUrl(window.location.pathname, window.location.origin);

  newUrl.searchParams.set("popup", "iframe");
  newUrl.searchParams.set("title", "Appointment");
  newUrl.searchParams.set("url", encodeURIComponent(url.href));

  return newUrl;
};

/**
 * Rewrites a link to a relative app path if it is an app link, or returns the original link otherwise.
 * Handles special cases for Apero links (opens in popup) and removes 'stay=false' query param.
 * @param link - The link to rewrite (string).
 * @returns {string} The rewritten link (relative path for app links, or absolute URL for others).
 */
export function rewriteAppLink(link?: string): string {
  if (!link) return "";
  if (link.startsWith("#")) return link;

  try {
    let url = saferUrl(link, window.location.origin);

    if (isAperoLink(url)) {
      url = toAppIframePopup(url);
    }

    // remove stay=false from url
    url.searchParams.delete("stay");

    if (isAppLink(url)) {
      // Normalize exactly two leading slashes to one slash to prevent issue when customer.io messages are sent with double slashes
      // https://9amhealth.slack.com/archives/C01SKQAU4PP/p1749724866447269
      // For example: '//path' becomes '/path'
      const normalizedPathname = url.pathname.replace(/^\/\/(?!\/)/, "/");
      return normalizedPathname + url.search + url.hash;
    }

    return url.href;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`Error rewriting link: ${link}`, e);
    return "";
  }
}
